import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import {
  LayoutDashboard, Car, Users, MessageSquare, Settings, LogOut,
  Clock, Star, BarChart3, Globe, Wallet, Phone, Clock3, Trash2, Megaphone, Server, Image, FileText
} from 'lucide-react';

// Import admin components
import InventoryManagement from '@/components/admin/InventoryManagement';
import TestDrivesManagement from '@/components/admin/TestDrivesManagement';
import LeadsCenterManagement from '@/components/admin/LeadsCenterManagement';
import ReviewsManagement from '@/components/admin/ReviewsManagement';
import SEOManagement from '@/components/admin/SEOManagement';
import AnalyticsDashboard from '@/components/admin/AnalyticsDashboard';
import SiteSettingsManagement from '@/components/admin/SiteSettingsManagement';
import ContentManagement from '@/components/admin/ContentManagement';
import DealerCenterManagement from '@/components/admin/DealerCenterManagement';
import HappyCustomersManagement from '@/components/admin/HappyCustomersManagement';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchStats();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'inventory', label: 'Inventory', icon: Car },
    { id: 'dealercenter', label: 'DealerCenter', icon: Server },
    { id: 'test-drives', label: 'Test Drives', icon: Clock, badge: stats?.pending_test_drives },
    { id: 'leads', label: 'Leads', icon: Users, badge: (stats?.request_info_leads || 0) + (stats?.marketing_leads || 0) + (stats?.unread_messages || 0) },
    { id: 'content', label: 'Content', icon: FileText },
    { id: 'reviews', label: 'Reviews', icon: Star },
    { id: 'happy-customers', label: 'Happy Customers', icon: Image },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'seo', label: 'SEO', icon: Globe },
    { id: 'site-settings', label: 'Site Settings', icon: Settings },
    { id: 'settings', label: 'Account', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#0a1628] flex">
      {/* Sidebar */}
      <div className="w-64 bg-[#132238] border-r border-[#1a2d47] flex flex-col">
        <div className="p-6 border-b border-[#1a2d47]">
          <h1 className="text-xl font-bold text-white">Admin Panel</h1>
          <p className="text-[#a8b8d0] text-sm">We Have Your Car</p>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                activeTab === item.id
                  ? 'bg-[#1a4d8c] text-white'
                  : 'text-[#a8b8d0] hover:bg-[#1a2d47] hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <item.icon size={20} />
                <span>{item.label}</span>
              </div>
              {item.badge > 0 && (
                <span className="bg-[#ff6b35] text-white text-xs px-2 py-0.5 rounded-full">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
        
        <div className="p-4 border-t border-[#1a2d47]">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-[#a8b8d0] hover:text-white hover:bg-red-500/10 rounded-lg transition-colors"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-8 overflow-y-auto">
        {/* Dashboard Overview */}
        {activeTab === 'dashboard' && (
          <div>
            <h1 className="text-2xl font-bold text-white mb-6">Dashboard Overview</h1>
            
            {/* Stats Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard 
                icon={Car} 
                label="Active Vehicles" 
                value={stats?.active_vehicles || 0} 
                subtext={`${stats?.total_vehicles || 0} total`}
                color="#1a4d8c"
              />
              <StatCard 
                icon={Clock} 
                label="Test Drives" 
                value={stats?.pending_test_drives || 0}
                subtext="Pending"
                color="#ff6b35"
              />
              <StatCard 
                icon={Users} 
                label="Total Leads" 
                value={(stats?.request_info_leads || 0) + (stats?.marketing_leads || 0) + (stats?.unread_messages || 0)}
                subtext="All sources"
                color="#ffc107"
              />
              <StatCard 
                icon={MessageSquare} 
                label="Messages" 
                value={stats?.unread_messages || 0}
                subtext="Unread"
                color="#22c55e"
              />
            </div>

            {/* Quick Actions */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <QuickAction 
                icon={Car}
                title="Manage Inventory"
                desc="Add, edit, or remove vehicle listings"
                onClick={() => setActiveTab('inventory')}
              />
              <QuickAction 
                icon={Clock}
                title="Test Drives"
                desc={`${stats?.pending_test_drives || 0} pending requests`}
                onClick={() => setActiveTab('test-drives')}
              />
              <QuickAction 
                icon={Users}
                title="Leads Center"
                desc={`${(stats?.request_info_leads || 0) + (stats?.marketing_leads || 0) + (stats?.unread_messages || 0)} total leads`}
                onClick={() => setActiveTab('leads')}
              />
            </div>
          </div>
        )}

        {/* Inventory - Using imported component */}
        {activeTab === 'inventory' && <InventoryManagement />}

        {/* DealerCenter Integration */}
        {activeTab === 'dealercenter' && <DealerCenterManagement />}

        {/* Test Drives - Using imported component */}
        {activeTab === 'test-drives' && <TestDrivesManagement />}

        {/* Leads Center - Unified leads, messages, and marketing opt-ins */}
        {activeTab === 'leads' && <LeadsCenterManagement />}

        {/* Reviews - Using imported component */}
        {activeTab === 'reviews' && <ReviewsManagement />}

        {/* Happy Customers Gallery */}
        {activeTab === 'happy-customers' && <HappyCustomersManagement />}

        {/* Analytics - Using imported component */}
        {activeTab === 'analytics' && <AnalyticsDashboard />}

        {/* SEO - Using imported component */}
        {activeTab === 'seo' && <SEOManagement />}

        {/* Content Management - About Us & Blog */}
        {activeTab === 'content' && <ContentManagement />}

        {/* Site Settings - Using imported component */}
        {activeTab === 'site-settings' && <SiteSettingsManagement />}

        {/* Settings */}
        {activeTab === 'settings' && (
          <div>
            <h1 className="text-2xl font-bold text-white mb-6">Settings</h1>
            
            <div className="max-w-md bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
              <h2 className="text-lg font-bold text-white mb-4">Change Password</h2>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <input
                  type="password"
                  placeholder="Current Password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  required
                  className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white"
                />
                <input
                  type="password"
                  placeholder="New Password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  required
                  className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white"
                />
                <input
                  type="password"
                  placeholder="Confirm New Password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  required
                  className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white"
                />
                <button type="submit" disabled={changingPassword} className="btn-accent w-full disabled:opacity-50">
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </form>
            </div>

            {/* Business Info */}
            <div className="mt-6 max-w-md bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
              <h2 className="text-lg font-bold text-white mb-4">Business Information</h2>
              <div className="space-y-3 text-sm">
                <p className="flex items-center gap-2 text-[#a8b8d0]">
                  <Phone size={16} className="text-[#ff6b35]" />
                  (512) 666-0016
                </p>
                <p className="flex items-center gap-2 text-[#a8b8d0]">
                  <Wallet size={16} className="text-[#ff6b35]" />
                  8203 Bluff Springs Rd, Unit 4, Austin, TX
                </p>
                <p className="flex items-center gap-2 text-[#a8b8d0]">
                  <Clock3 size={16} className="text-[#ff6b35]" />
                  Mon-Sat: 10AM - 6PM
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subtext, badge, color }) => (
  <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
    <div className="flex items-center gap-3 mb-2">
      <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}30` }}>
        <Icon size={20} style={{ color }} />
      </div>
      <span className="text-[#a8b8d0]">{label}</span>
    </div>
    <p className="text-3xl font-bold text-white">{value}</p>
    {subtext && <p className="text-[#a8b8d0] text-sm">{subtext}</p>}
    {badge && <span className="text-[#ff6b35] text-sm">{badge}</span>}
  </div>
);

// Quick Action Component
const QuickAction = ({ icon: Icon, title, desc, onClick }) => (
  <button
    onClick={onClick}
    className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47] hover:border-[#1a4d8c] transition-colors text-left"
  >
    <Icon size={24} className="text-[#ff6b35] mb-3" />
    <h3 className="text-white font-medium mb-1">{title}</h3>
    <p className="text-[#a8b8d0] text-sm">{desc}</p>
  </button>
);

export default AdminDashboard;
